/**
 * Payment-method chips — a name and a selected state that glows.
 *
 * Janeks: *"Payment provider selection - proper chips ... Selected is
 * Highlighted + glow."* and then *"Remove pyament method icons, leave just the
 * name."*
 *
 * ─── Why this is NOT in `core/` ─────────────────────────────────────────────
 * It should be, and it cannot be from here. This is a GAME repo
 * (`t-game/after-humanity`, with `t-game/core` as upstream), where
 * `frontend/src/core/**` is template-owned: CLAUDE.md forbids editing it and
 * `.claude/hooks/validate-core-edit.sh` hard-blocks the write (exit 2), because
 * an edit here would conflict on every merge from upstream.
 *
 * So it lives in `engine/` and is written to LIFT: no import from anywhere
 * under `base-view/`, no game state, no `bv-` selectors in its own markup
 * beyond the ones it owns, and the caller passes in the methods and the
 * selection. Moving it to `core/ui/` is a copy of this file, a copy of the
 * `.bv-chip` block out of `styles/commerce.css.ts`, and deleting these two —
 * which is a separate MR against the template repo.
 *
 * ─── No logos: name only ────────────────────────────────────────────────────
 * Janeks, 2026-09-18: *"Remove pyament method icons, leave just the name."*
 *
 * The first version drew a generic built-in glyph per method — a card, a phone
 * with a radio arc, a phone being tapped — because the real Visa / Mastercard
 * / BLIK / Apple Pay marks are not in this repo and are not ours to
 * approximate. Those glyphs and the `logoUrl` slot they fell back to are
 * DELETED rather than left switched off: an unrendered art path is exactly the
 * unreachable-but-type-correct code this repo keeps getting bitten by, and a
 * real brand mark arriving later wants a considered layout anyway, not a 24x16
 * hole someone left behind.
 */

/** What a chip needs to draw itself. Deliberately not the game's `PaymentMethod`. */
export interface PaymentChipMethod {
  id: string;
  label: string;
  enabled: boolean;
}

export interface PaymentChipOptions {
  /** Currently chosen id, or null for none. */
  selectedId: string | null;
  /** Disables every chip (a purchase in flight). Distinct from a method's own `enabled`. */
  busy?: boolean;
}

/**
 * The chip row.
 *
 * `<button role="radio">` rather than `<input type="radio">`: the version this
 * replaces styled a bare native radio dot beside a word, which cannot carry a
 * selected-state background or a glow. The keyboard contract is kept by hand —
 * `role="radiogroup"`
 * on the row, `aria-checked` per chip, and `tabindex` on the selected one only
 * so Tab reaches the group once and arrows move inside it (what a native radio
 * group does for free, and what a row of plain buttons would break).
 *
 * `data-pay-method` is the hook the caller's delegated click listener reads —
 * the same attribute name the radios used, so the call site did not change.
 */
export function paymentChipsHtml(
  methods: PaymentChipMethod[],
  opts: PaymentChipOptions,
): string {
  const chips = methods.map((m) => {
    const on = m.id === opts.selectedId;
    const dead = !m.enabled || !!opts.busy;
    const cls = ['bv-chip'];
    if (on) cls.push('bv-chip-on');
    if (!m.enabled) cls.push('bv-chip-off');
    return `<button type="button" class="${cls.join(' ')}"
      role="radio" aria-checked="${on}" ${dead ? 'disabled' : ''}
      tabindex="${on ? 0 : -1}"
      data-pay-method="${escapeAttr(m.id)}">
      <span class="bv-chip-label">${escapeHtml(m.label)}</span>
      ${m.enabled ? '' : '<span class="bv-chip-soon">soon</span>'}
    </button>`;
  }).join('');
  return `<div class="bv-chips" role="radiogroup" aria-label="Payment method">${chips}</div>`;
}

/**
 * Arrow-key movement inside a chip row, for the caller to wire to `keydown`.
 *
 * Returns the id to select, or null if the key was not one this handles — so
 * the caller can leave every other key alone. Skips disabled methods, and
 * wraps, which is what a native radio group does.
 */
export function chipKeyTarget(
  key: string,
  methods: PaymentChipMethod[],
  selectedId: string | null,
): string | null {
  const step = key === 'ArrowRight' || key === 'ArrowDown' ? 1
    : key === 'ArrowLeft' || key === 'ArrowUp' ? -1 : 0;
  if (step === 0) return null;
  const usable = methods.filter((m) => m.enabled);
  if (usable.length === 0) return null;
  const at = usable.findIndex((m) => m.id === selectedId);
  // From nothing selected, either direction lands on the first usable chip.
  if (at === -1) return usable[0]!.id;
  const next = (at + step + usable.length) % usable.length;
  return usable[next]!.id;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}

/** Attribute context — same escaping, named separately so call sites read clearly. */
function escapeAttr(s: string): string {
  return escapeHtml(s);
}
